import React from 'react';
import ReactDOM from 'react-dom/client';
import { ApolloClient, HttpLink, InMemoryCache } from '@apollo/client';
import { ApolloProvider } from '@apollo/client';
import {
  BrowserRouter,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';

import './index.css';

import { App } from './App';
import { createKbLink } from '@/apollo/links';
import config from './config';
import { ErrorBoundary } from './components/ErrorBoundary';
import { FatalErrorFallback } from './components/FatalErrorFallback';

import { SnackbarProvider } from 'notistack';
import { CustomSnackbar } from './components/CustomSnackBar.tsx';

import { PostHogProvider } from 'posthog-js/react';

const cache = new InMemoryCache();

const httpLink = new HttpLink({ uri: config.kbUrl + config.kbEndpoint });

const client = new ApolloClient({
  link: createKbLink(httpLink),
  cache,
});

import * as Sentry from '@sentry/react';
import { scrubSentryEvent } from './util/sentryState';

Sentry.init({
  dsn: config.sentry_dsn,
  sendDefaultPii: false,
  // debug: !config.env.production,
  environment: config.env.production
    ? 'Prod'
    : config.env.staging
    ? 'Staging'
    : 'Dev',
  integrations: [
    // Console calls may print patient objects; keep them out of breadcrumbs.
    Sentry.breadcrumbsIntegration({ console: false }),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      maskAllText: true,
      maskAllInputs: true,
      blockAllMedia: true,
    }),
  ],
  beforeSend: (event) => scrubSentryEvent(event),
  tracesSampleRate: 1.0,
  tracePropagationTargets: [config.apiUrl, config.kbUrl],
  replaysSessionSampleRate: 1.0,
  replaysOnErrorSampleRate: 1.0,
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <ErrorBoundary fallback={<FatalErrorFallback />}>
    <PostHogProvider
      apiKey={config.posthog.key}
      options={{
        api_host: config.posthog.host,
        ui_host: 'https://eu.posthog.com',
        persistence: 'localStorage',
        disable_cookie: true,
      }}
    >
      <ApolloProvider client={client}>
        <BrowserRouter>
          <SnackbarProvider
            Components={{
              success: CustomSnackbar,
            }}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            maxSnack={1}
          >
            <App />
          </SnackbarProvider>
        </BrowserRouter>
      </ApolloProvider>
    </PostHogProvider>
  </ErrorBoundary>,
);
